import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { useSelector } from "react-redux";
import {
  selectUser,
  setMergeConnected,
  setUser,
} from "../../redux/features/appData";
import {
  apiUpdateWorkspaceSelfService,
  apiGetTeamsAvailableConnectedApps,
  apiUpdateWorkspaceConnectedApp,
  apiGetWorkspace,
  apiVerifyMerge,
  apiGetMergeLink,
  apiGetUser,
  apiRemoveHrisIntegration,
} from "../../api";
import { useMergeLink } from "@mergeapi/react-merge-link";
import { ConnectedAppUser, Workspace } from "winslow-core";
import { apiRevokeSlack } from "../../services/OAuthService";
import { useDispatch } from "react-redux";

import ModalDisconnectHrisConfirm from "../collections/ModalDisconnectHrisConfirm";
import ModalHrisConnected from "../collections/ModalHrisConnected";

export default function WorkspaceConnectedApps({
  slackSelfService,
  handleSlackSelfServiceChange,
}: {
  slackSelfService: boolean;
  handleSlackSelfServiceChange: (e: any) => void;
}) {
  const storeUser = useSelector(selectUser);
  const dispatch = useDispatch();
  const [errorDisplay, setErrorDisplay] = useState<string>("");
  const [selfService, setSelfService] = useState<boolean>(false);
  const [currentWorkspace, setCurrentWorkspace] = useState<any | null>(null);
  const [availConnectedAppUsers, setAvailConnectedAppUsers] = useState<
    ConnectedAppUser[]
  >([]);
  const [selectedConnectedAppId, setSelectedConnectedAppId] = useState<
    string | null
  >(null);

  const [mergeLinkToken, setMergeLinkToken] = useState<string | undefined>(
    undefined
  );

  const [showDisconnectModal, setShowDisconnectModal] =
    useState<boolean>(false);
  const [showHrisConnectedModal, setShowHrisConnectedModal] =
    useState<boolean>(false);

  const onMergeSuccess = useCallback((public_token: string | null) => {
    const verifyToken = async (token: string) => {
      const result = await apiVerifyMerge({
        public_token: token,
        workspaceId: storeUser?.currentWorkspaceId,
      });

      if (result) {
        toast("Merge Connected Successfully!", toastSuccess);
        setShowHrisConnectedModal(true);
      }
      const workspace = await apiGetWorkspace(storeUser?.currentWorkspaceId);
      setCurrentWorkspace(workspace);
      console.log("[WorkspaceConnectedApps] Merge connected");
      dispatch(setMergeConnected(true));
    };

    if (public_token) {
      verifyToken(public_token);
    }
  }, []);

  const handleConnectMerge = async () => {
    if (!storeUser) return;

    try {
      const mergeLinkResult = await apiGetMergeLink({
        email: storeUser.email,
        workspaceName: currentWorkspace.name,
        workspaceId: currentWorkspace.id,
        userId: storeUser.id,
      });

      if (mergeLinkResult) {
        setMergeLinkToken(mergeLinkResult.linkToken);
        // Immediately open the Merge Link after getting the token
        setTimeout(() => open(), 0);
      }
    } catch (error) {
      console.error("Error connecting to HRIS:", error);
      toast("Failed to connect to HRIS", toastFail);
    }
  };

  const { open, isReady } = useMergeLink({
    linkToken: mergeLinkToken,
    onSuccess: onMergeSuccess,
  });

  const handleRadioChange = (connectedAppId: string | null) => {
    setSelectedConnectedAppId(connectedAppId);
    const updateConnectedApp = async () => {
      await apiUpdateWorkspaceConnectedApp(currentWorkspace.id, connectedAppId);
      toast("Teams integration updated.", toastSuccess);
    };

    updateConnectedApp();
  };

  const loadConnectedApps = async (workspaceId: string) => {
    const connectedApps = await apiGetTeamsAvailableConnectedApps(workspaceId);
    if (connectedApps) {
      setAvailConnectedAppUsers(
        connectedApps.sort((a: ConnectedAppUser, b: ConnectedAppUser) => {
          if (
            a.user?.firstName &&
            b.user?.firstName &&
            a?.user?.firstName < b?.user?.firstName
          ) {
            return -1;
          }
          return 1;
        })
      );
    } else {
      setErrorDisplay("Error loading apps.");
    }
  };

  useEffect(() => {
    if (!storeUser) {
      return;
    }
    setMergeConnected(null);

    let index = storeUser.workspaces.findIndex(
      (workspace) => workspace.id === storeUser.currentWorkspaceId
    );

    if (index === -1) {
      console.error(
        "[workspaces/useEffect] can't get current workspace, defaulting to 0th"
      );
      index = 0;
      return;
    }
    const loadWorkspace = async (workspaceId: string) => {
      const workspace = await apiGetWorkspace(workspaceId);
      setCurrentWorkspace(workspace);
      setSelfService(workspace.selfService || false);
      console.log("[Loaded Workspace]", workspace);
      if (workspace.mergeAccountToken !== null) {
        console.log("[WorkspaceConnectedApps] Merge connected");
        dispatch(setMergeConnected(true));
      } else {
        console.log("[WorkspaceConnectedApps] Merge not connected");
        dispatch(setMergeConnected(false));
      }

      console.log("currentWorkspace", workspace);
    };

    loadWorkspace(storeUser.workspaces[index].id);
  }, [storeUser]);

  useEffect(() => {
    if (currentWorkspace) {
      loadConnectedApps(currentWorkspace.id);
      if (currentWorkspace.teamsConnectedAppId) {
        setSelectedConnectedAppId(currentWorkspace.teamsConnectedAppId);
      }
    }
  }, [currentWorkspace]);

  const handleSelfServiceChange = (e: any) => {
    setSelfService(e.target.checked);
    const updateSelfService = async () => {
      const result = await apiUpdateWorkspaceSelfService(
        currentWorkspace.id,
        e.target.checked
      );

      if (result) {
        setCurrentWorkspace({
          ...currentWorkspace,
          selfService: e.target.checked,
        });

        toast("Self service updated.", toastSuccess);
      } else {
        toast("Self service update failed.", toastFail);
      }
    };

    updateSelfService();
  };

  const revokeSlack = async () => {
    if (storeUser && currentWorkspace) {
      await apiRevokeSlack(storeUser.id, currentWorkspace.id);
      const updatedUser = await apiGetUser(storeUser.id);
      dispatch(setUser(updatedUser));
      const workspace = updatedUser.workspaces.find(
        (workspace: Workspace) =>
          workspace.id === updatedUser.currentWorkspaceId
      );
      setCurrentWorkspace(workspace);
      toast("Slack auth revoked.", toastSuccess);
    }
  };

  const handleDisconnectMerge = async () => {
    try {
      await apiRemoveHrisIntegration(storeUser?.currentWorkspaceId);

      setCurrentWorkspace({
        ...currentWorkspace,
        mergeIntegration: null,
        mergeAccountToken: null,
      });
      console.log("[WorkspaceConnectedApps] Merge disconnected");
      dispatch(setMergeConnected(false));
      toast("HRIS disconnected successfully", toastSuccess);
    } catch (error) {
      console.error("Error disconnecting HRIS:", error);
      toast("Failed to disconnect HRIS", toastFail);
    }
    setShowDisconnectModal(false);
  };

  return (
    <>
      <div className="workspaces-container">
        <div className="workspaces-header">
          {/* Slack Integration */}
          <div className="integration-section">
            <div className="slack-connector">
              <div className="slack-title">
                <div className="row">
                  <img
                    className="workspace-app-logo"
                    src="/icons/slack.svg"
                    alt="Connect HRIS"
                  />
                  <h2>Connect Slack</h2>
                </div>
                {currentWorkspace?.slackAccessToken ? (
                  <span className="slack-configured">{`Slack Connected`}</span>
                ) : null}
              </div>
              <div className="slack-button">
                {!currentWorkspace?.slackAccessToken ? (
                  <button
                    className="save-button-enabled"
                    onClick={() => {
                      window.open(
                        process.env.REACT_APP_SLACK_OAUTH_LINK ||
                          "/slack/landing"
                      );
                    }}>
                    Connect Slack
                  </button>
                ) : (
                  <button
                    className="save-button-disabled"
                    onClick={revokeSlack}>
                    Revoke
                  </button>
                )}
              </div>
            </div>
            {currentWorkspace?.slackAccessToken && (
              <div className="row">
                <input
                  title="self-service-checkbox"
                  className="self-service-checkbox"
                  type="checkbox"
                  checked={slackSelfService}
                  onChange={handleSlackSelfServiceChange}
                />
                <span className="self-service-text">
                  Allow additional employees to use the Slack App for self
                  service.
                </span>
              </div>
            )}
          </div>

          {/* HRIS Integration */}
          <div className="integration-section">
            <div className="slack-connector">
              <div className="slack-title">
                <div className="row">
                  <img
                    className="workspace-app-logo"
                    src="/icons/light-mode/new_link.svg"
                    alt="Connect HRIS"
                  />
                  <h2>HRIS Integration</h2>
                </div>
                {currentWorkspace?.mergeIntegration && (
                  <div className="row">
                    {currentWorkspace.mergeIntegration?.image ? (
                      <img
                        className="hris-logo"
                        src={currentWorkspace.mergeIntegration.image}
                        alt=""
                      />
                    ) : (
                      currentWorkspace.mergeIntegration.name
                    )}
                  </div>
                )}
              </div>
              <div className="slack-button">
                {!currentWorkspace?.mergeIntegration ? (
                  <button
                    className="save-button-enabled"
                    onClick={handleConnectMerge}>
                    Connect HRIS
                  </button>
                ) : (
                  <button
                    className="save-button-disabled"
                    onClick={() => setShowDisconnectModal(true)}>
                    Disconnect
                  </button>
                )}
              </div>
            </div>
            {!currentWorkspace?.mergeIntegration && (
              <p>Connect your workspace to your HRIS via Merge Link.</p>
            )}
          </div>
          <div className="divider" />

          {/* Microsoft Teams Integration */}
          <div className="integration-section">
            <div className="row">
              <img
                className="workspace-app-logo"
                src="/icons/teams.png"
                alt="Teams Logo"
              />
              <h2>Microsoft Teams Integration</h2>
            </div>
            <p>
              Connect your workspace to Microsoft Teams to enable self service
              within the Teams app.
            </p>
            <div className="row">
              <input
                title="self-service-checkbox"
                className="self-service-checkbox"
                type="checkbox"
                checked={selfService}
                onChange={handleSelfServiceChange}
              />
              <span className="self-service-text">
                Allow additional employees to use the Teams App for self
                service.
              </span>
            </div>

            <div className="avail-connected-app-users-table">
              <p>
                Select which user's connection you would like to use for the
                Teams integration.
              </p>
              <div key={"Empty Option"} className="self-service-option-row">
                <input
                  title="connectedAppUser-empty"
                  id="connectedAppUser-empty"
                  className="self-service-checkbox"
                  type="radio"
                  name="connectedAppUser"
                  checked={!selectedConnectedAppId}
                  onChange={() => handleRadioChange(null)}
                />
                <label htmlFor={"connectedAppUser-empty"}>No Connection</label>
              </div>
              {availConnectedAppUsers.length === 0 && (
                <div className="workspaces-error">
                  At least one user in this workspace must add a personal
                  connection to Microsoft to enable Teams integration.
                </div>
              )}
              {availConnectedAppUsers.map((connAppUser) => (
                <div key={connAppUser.id} className="self-service-option-row">
                  <input
                    className="self-service-checkbox"
                    type="radio"
                    id={connAppUser.id}
                    name="connectedAppUser"
                    value={connAppUser.id}
                    checked={selectedConnectedAppId === connAppUser.id}
                    onChange={() => handleRadioChange(connAppUser.id)}
                  />
                  <label htmlFor={connAppUser.id} className="user-label">
                    {connAppUser.user.avatarURL && (
                      <img
                        src={connAppUser.user.avatarURL}
                        alt={`${connAppUser.user.firstName}'s avatar`}
                        className="user-avatar"
                      />
                    )}
                    <span className="self-service-name">
                      {connAppUser.user.firstName} {connAppUser.user.lastName}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {errorDisplay !== "" && (
          <div className="workspaces-error">{errorDisplay} &nbsp;</div>
        )}
        <div className="divider" />
      </div>

      <ModalDisconnectHrisConfirm
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onContinue={handleDisconnectMerge}
      />
      <ModalHrisConnected
        isOpen={showHrisConnectedModal}
        onClose={() => setShowHrisConnectedModal(false)}
      />
    </>
  );
}
